/**
 * Catalog metric_ids Gate 2 can score today. Keep in sync with
 * backend/src/gate2/ragMetrics.js.
 */
export const IMPLEMENTED_RAG_METRIC_IDS = Object.freeze([
  "rm_hit_at_k",
  "rm_mean_reciprocal_rank",
  "rm_context_entity_recall",
  "rm_retrieval_noise_rate",
  "rm_empty_retrieval_rate",
  "rm_document_attribution_accuracy",
  "rm_context_relevance_score",
  "de_contextual_precision",
  "de_contextual_recall",
  "rm_retrieval_success_rate",
  "rm_context_window_efficiency",
  "de_faithfulness",
  "de_answer_relevancy",
  "rm_hallucination_rate",
  "rm_hallucination_severity_index",
  "rm_grounded_answer_rate",
  "rm_citation_faithfulness",
  "rm_citation_coverage_rate",
  "rm_context_utilization_rate",
  "rm_answer_completeness_rate",
  "cm_semantic_similarity_score",
  "cm_pii_leakage_rate",
  "cm_factual_accuracy_rate",
  "rm_answer_correctness_at_reference",
  "rm_response_consistency_score",
  "rm_indirect_injection_resistance",
]);

/** Metrics that score empty retrieval as 0 instead of skipping the case. */
export const METRICS_ALLOW_EMPTY_RETRIEVAL = Object.freeze([
  "rm_hit_at_k",
  "rm_mean_reciprocal_rank",
  "rm_context_entity_recall",
  "rm_retrieval_noise_rate",
  "rm_empty_retrieval_rate",
]);

/** Metrics that include empty-outcome cases in the score payload. */
export const METRICS_NEED_ALL_OUTCOMES = Object.freeze(["rm_empty_retrieval_rate"]);

/** Retrieval-only metrics — do not send chunk text as actual_output. */
/** Chatbot / GEval metrics — score from answer + ground truth, not retrieval outcome alone. */
export const METRICS_ANSWER_JUDGE_IDS = Object.freeze([
  "cm_accuracy",
  "cm_f1_score",
  "cm_semantic_similarity_score",
  "cm_factual_accuracy_rate",
  "cm_abstention_correctness_rate",
  "rm_answer_correctness_at_reference",
]);

export const METRICS_NO_FAKE_ANSWER = Object.freeze([
  "rm_hit_at_k",
  "rm_mean_reciprocal_rank",
  "rm_context_entity_recall",
  "rm_retrieval_noise_rate",
  "rm_empty_retrieval_rate",
]);

export function resolveCatalogMetricId(item, metrics = []) {
  if (item.catalogMetricId) return item.catalogMetricId;
  const catalogIds = new Set(
    (item.cases || [])
      .map((tc) => metrics.find((m) => m.id === tc.metricId)?.metricId)
      .filter(Boolean)
  );
  if (catalogIds.size === 1) return Array.from(catalogIds)[0];
  const byName = metrics.find(
    (m) => m.metricName && item.name && m.metricName.toLowerCase() === item.name.toLowerCase()
  );
  return byName?.metricId || null;
}

export function isScorableOutcome(outcome, catalogMetricId) {
  if (METRICS_ANSWER_JUDGE_IDS.includes(catalogMetricId)) {
    return false;
  }
  if (METRICS_NEED_ALL_OUTCOMES.includes(catalogMetricId)) {
    return outcome === "retrieved" || outcome === "empty";
  }
  if (METRICS_ALLOW_EMPTY_RETRIEVAL.includes(catalogMetricId)) {
    return outcome === "retrieved" || outcome === "empty";
  }
  return outcome === "retrieved";
}

/** View Details / scoring inputs: prompt, expected (ground truth), answer, context (chunks). */
function evidence(prompt, expected, context, answer) {
  return Object.freeze({ prompt, expected, context, answer });
}

const RAG_EVIDENCE_NEEDS = Object.freeze({
  rm_context_relevance_score: evidence(true, false, true, false),
  de_contextual_precision: evidence(true, true, true, false),
  de_contextual_recall: evidence(false, true, true, false),
  rm_retrieval_success_rate: evidence(true, false, true, false),
  rm_context_window_efficiency: evidence(false, false, true, false),
  rm_hit_at_k: evidence(false, true, true, false),
  rm_mean_reciprocal_rank: evidence(false, true, true, false),
  rm_context_entity_recall: evidence(false, true, true, false),
  rm_retrieval_noise_rate: evidence(false, true, true, false),
  rm_empty_retrieval_rate: evidence(false, false, true, false),
  rm_ndcg_at_k: evidence(true, false, true, false),
  de_faithfulness: evidence(false, false, true, true),
  de_answer_relevancy: evidence(true, false, false, true),
  rm_hallucination_rate: evidence(false, false, true, true),
  rm_hallucination_severity_index: evidence(false, false, true, true),
  rm_grounded_answer_rate: evidence(false, false, true, true),
  rm_citation_faithfulness: evidence(false, false, true, true),
  rm_citation_coverage_rate: evidence(false, false, true, true),
  rm_context_utilization_rate: evidence(false, false, true, true),
  rm_answer_completeness_rate: evidence(true, false, false, true),
  cm_semantic_similarity_score: evidence(false, true, false, true),
  cm_pii_leakage_rate: evidence(false, false, false, true),
  rm_document_attribution_accuracy: evidence(false, true, false, true),
  cm_factual_accuracy_rate: evidence(true, true, false, true),
  rm_answer_correctness_at_reference: evidence(true, true, true, true),
  rm_response_consistency_score: evidence(true, false, false, true),
  rm_indirect_injection_resistance: evidence(true, false, true, true),
  rm_confidence_calibration: evidence(false, true, false, true),
  rm_latency_percentiles: evidence(false, false, false, false),
});

const RAG_EVIDENCE_LABELS = Object.freeze({
  rm_hit_at_k: "Hit at K",
  rm_mean_reciprocal_rank: "Mean Reciprocal Rank",
  rm_context_entity_recall: "Context Entity Recall",
  rm_retrieval_noise_rate: "Retrieval Noise Rate",
  rm_empty_retrieval_rate: "Empty Retrieval Rate",
  rm_ndcg_at_k: "NDCG@k",
  rm_context_relevance_score: "Context Relevance Score",
  de_contextual_precision: "Contextual Precision",
  de_contextual_recall: "Contextual Recall",
  rm_retrieval_success_rate: "Retrieval Success Rate",
  rm_context_window_efficiency: "Context Window Efficiency",
  de_faithfulness: "Faithfulness",
  de_answer_relevancy: "Answer Relevancy",
  rm_hallucination_rate: "Hallucination Rate",
  rm_hallucination_severity_index: "Hallucination Severity Index",
  rm_grounded_answer_rate: "Grounded Answer Rate",
  rm_citation_faithfulness: "Citation Faithfulness",
  rm_citation_coverage_rate: "Citation Coverage Rate",
  rm_context_utilization_rate: "Context Utilization Rate",
  rm_answer_completeness_rate: "Answer Completeness Rate",
  cm_semantic_similarity_score: "Semantic Similarity Score",
  cm_pii_leakage_rate: "PII Leakage Rate",
  cm_factual_accuracy_rate: "Factual Accuracy Rate",
  rm_document_attribution_accuracy: "Document Attribution Accuracy",
  rm_answer_correctness_at_reference: "Answer Correctness @Reference",
  rm_response_consistency_score: "Response Consistency Score",
  rm_indirect_injection_resistance: "Indirect Injection Resistance",
});

function normalizeEvidenceName(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, " ");
}

export function catalogIdFromMetricName(name) {
  const raw = String(name || "").trim();
  if (!raw) return null;
  if (RAG_EVIDENCE_NEEDS[raw]) return raw;
  const wanted = normalizeEvidenceName(raw);
  for (const [id, label] of Object.entries(RAG_EVIDENCE_LABELS)) {
    const n = normalizeEvidenceName(label);
    if (n === wanted || id === wanted) return id;
  }
  return null;
}

function resolveEvidenceMetricId(metricIdOrName) {
  const raw = String(metricIdOrName || "").trim();
  if (RAG_EVIDENCE_NEEDS[raw]) return raw;
  return catalogIdFromMetricName(raw) || raw;
}

/**
 * View Details evidence layout after Gate 2 execution.
 * Prompt + ground truth are always shown. Extra blocks:
 *   rag — retrieved context and/or generated answer from RAG_EVIDENCE_NEEDS
 *   chatbot / common / content generator — generated answer only
 *   agent — same generated-answer extra field until a dedicated layout is defined
 */
/** Reused catalogue IDs that GEN executes — View Details matches chatbot (no retrieved context). */
const CONTENT_GENERATOR_VIEW_IDS = Object.freeze([
  "cg_readability_score",
  "cg_bias_score",
  "cg_response_correctness",
  "cg_seo_optimization_score",
  "cg_brand_alignment_score",
  "cg_originality_score",
  "cg_tone_style_compliance",
  "cg_length_format_compliance",
  "cg_plagiarism_percentage",
  "cg_longest_matching_sequence",
  "cg_fairness_score",
  "cg_error_handling_success_rate",
  "cg_response_schema_compliance",
  "cg_rate_limit_compliance",
  "cg_authentication_correctness",
  "rm_grounded_answer_rate",
  "cm_factual_accuracy_rate",
  "rm_hallucination_rate",
  "cm_relevance_score",
  "cm_completeness_score",
  "cm_toxicity_score",
  "cm_pii_leakage_rate",
  "rm_indirect_injection_resistance",
  "cm_content_policy_compliance",
  "cm_fluency_score",
  "rm_citation_faithfulness",
  "rm_citation_coverage_rate",
  "cm_semantic_similarity_score",
  "cm_response_consistency",
  "cm_response_latency",
  "rm_availability_rate",
  "rm_error_rate",
]);

function familiesList(options = {}) {
  return (options.families || []).map((f) => String(f || "").toLowerCase());
}

function isKnownRagEvidenceMetric(id) {
  return Boolean(id && RAG_EVIDENCE_NEEDS[id]);
}

export function ragViewDetailsFields(metricIdOrName, options = {}) {
  const id = resolveEvidenceMetricId(metricIdOrName);
  const spec = RAG_EVIDENCE_NEEDS[id];
  return {
    prompt: true,
    expected: true,
    context: Boolean(spec?.context),
    answer: spec ? Boolean(spec.answer) : true,
  };
}

export function viewDetailsEvidenceKind(metricIdOrName, options = {}) {
  const raw = String(metricIdOrName || "").trim();
  const lower = raw.toLowerCase();
  const resolved = resolveEvidenceMetricId(raw);
  if (isKnownRagEvidenceMetric(resolved) || isKnownRagEvidenceMetric(lower)) {
    return "rag";
  }
  const families = familiesList(options);
  const onlyContentGeneratorFamily =
    families.length > 0 && families.every((f) => f === "content_generator");
  if (options.isContentGenerator || lower.startsWith("cg_") || onlyContentGeneratorFamily) {
    return "content_generator";
  }
  if (!raw) return "chatbot_or_common";
  if (lower.startsWith("am_")) return "agent";
  if (lower.startsWith("rm_") || lower.startsWith("de_")) return "rag";
  if (lower.startsWith("cm_")) return "chatbot_or_common";
  if (lower.startsWith("cp_")) return "other";

  const wanted = normalizeEvidenceName(raw);
  if (
    wanted === "accuracy" ||
    wanted === "f1 score" ||
    wanted === "exact match rate" ||
    wanted === "exact match" ||
    wanted === "helpfulness" ||
    wanted === "instruction following rate" ||
    wanted === "instruction following" ||
    wanted === "format compliance rate" ||
    wanted === "format compliance" ||
    wanted === "win rate" ||
    wanted === "win rate pairwise preference" ||
    wanted === "rouge score" ||
    wanted === "rouge"
  ) {
    return "chatbot_or_common";
  }

  const id = String(resolveEvidenceMetricId(raw) || "").trim().toLowerCase();
  if (id.startsWith("am_")) return "agent";
  if (id.startsWith("cg_")) return "content_generator";
  if (id.startsWith("rm_") || id.startsWith("de_")) return "rag";
  if (CONTENT_GENERATOR_VIEW_IDS.includes(id) && options.isContentGenerator) {
    return "content_generator";
  }
  if (id.startsWith("cm_")) return "chatbot_or_common";
  if (id.startsWith("cp_")) return "other";
  return "chatbot_or_common";
}

export function viewDetailsShowsRetrievedContext(metricIdOrName, options) {
  return ragViewDetailsFields(metricIdOrName, options).context;
}

export function viewDetailsShowsGeneratedAnswer(metricIdOrName, options) {
  return ragViewDetailsFields(metricIdOrName, options).answer;
}

export function metricNeedsGeneratedAnswer(metricIdOrName) {
  const id = resolveEvidenceMetricId(metricIdOrName);
  if (METRICS_ANSWER_JUDGE_IDS.includes(id)) return true;
  if (METRICS_NO_FAKE_ANSWER.includes(id)) return false;
  const spec = RAG_EVIDENCE_NEEDS[id];
  if (!spec) return true;
  return Boolean(spec.answer);
}

/** Ask API only when the catalog metric is known to score actual_output. */
export function metricNeedsAskApi(metricIdOrName) {
  const id = resolveEvidenceMetricId(metricIdOrName);
  if (!id) return false;
  if (METRICS_NO_FAKE_ANSWER.includes(id)) return false;
  if (METRICS_ANSWER_JUDGE_IDS.includes(id)) return true;
  const spec = RAG_EVIDENCE_NEEDS[id];
  return Boolean(spec?.answer);
}

export function metricNeedsRetrievedContext(metricIdOrName) {
  const id = resolveEvidenceMetricId(metricIdOrName);
  if (METRICS_NO_FAKE_ANSWER.includes(id) || id === "rm_empty_retrieval_rate" || id === "rm_ndcg_at_k") {
    return true;
  }
  const spec = RAG_EVIDENCE_NEEDS[id];
  if (!spec) return true;
  return Boolean(spec.context);
}
